import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, User } from 'lucide-react';
import SmartImage from './SmartImage';
import { getAvatarEmoji } from './AvatarSelector';
import { resolveImageUrl as resolveImageUrlUtil } from '../utils/urlResolver';
import { fetchArtistSearchIndex, filterArtistSearchIndex } from '../utils/artistSearchIndex';

function ArtistSearchAvatar({ artist }) {
  const name = artist.displayName || 'Artist';
  if (artist.avatarId) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#fff7e8] to-[#fdebd4] text-lg">
        {getAvatarEmoji(artist.avatarId)}
      </div>
    );
  }
  const pic = artist.profilePicture ? resolveImageUrlUtil(artist.profilePicture) || artist.profilePicture : '';
  if (pic) {
    return (
      <SmartImage
        src={pic}
        alt=""
        className="h-full w-full object-cover"
        containerClassName="h-full w-full"
      />
    );
  }
  return (
    <div className="flex h-full w-full items-center justify-center bg-[#f4efe6] text-[#c9a574]">
      <User className="h-4 w-4" strokeWidth={1.75} aria-hidden />
    </div>
  );
}

export default function ArtistSearchCombobox({
  value,
  onChange,
  onExploreAll,
  placeholder = 'Search artists by name, bio, or art style…',
  className = '',
}) {
  const navigate = useNavigate();
  const listboxId = useId();
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const [index, setIndex] = useState([]);
  const [indexLoading, setIndexLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownAnchor, setDropdownAnchor] = useState(null);

  const results = useMemo(
    () => filterArtistSearchIndex(index, value, 12),
    [index, value]
  );

  const showDropdown = open && value.trim().length > 0;

  const ensureIndex = useCallback(async () => {
    if (index.length > 0 || indexLoading) return;
    setIndexLoading(true);
    try {
      const rows = await fetchArtistSearchIndex();
      setIndex(rows);
    } catch (err) {
      console.warn('[ArtistSearchCombobox] index load failed:', err);
      setIndex([]);
    } finally {
      setIndexLoading(false);
    }
  }, [index.length, indexLoading]);

  useEffect(() => {
    if (showDropdown) ensureIndex();
  }, [showDropdown, ensureIndex]);

  useEffect(() => {
    if (!showDropdown) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex(results.length > 0 ? 0 : -1);
  }, [showDropdown, results.length, value]);

  useLayoutEffect(() => {
    if (!showDropdown) {
      setDropdownAnchor(null);
      return undefined;
    }
    const update = () => {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDropdownAnchor({
        left: r.left,
        top: r.bottom + 6,
        width: r.width,
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showDropdown, value]);

  useEffect(() => {
    const onDocDown = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (e.target.closest('[data-artist-search-dropdown]')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  const goToArtist = (artist) => {
    if (!artist?.uid) return;
    setOpen(false);
    onChange('');
    navigate(`/artist-public-gallery/${artist.uid}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && activeIndex >= 0 && results[activeIndex]) {
        goToArtist(results[activeIndex]);
        return;
      }
      onExploreAll?.();
      setOpen(false);
      return;
    }
    if (!showDropdown) {
      if (e.key === 'ArrowDown' && value.trim()) {
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className={`relative min-w-0 flex-1 ${className}`.trim()}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-[#8b6914]"
        aria-hidden
      />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          ensureIndex();
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="auth-premium-input w-full rounded-full py-2 pl-9 pr-3 text-sm text-[#2f251b] placeholder:text-[#9a7a55] focus:outline-none"
        aria-label="Search artists"
        aria-expanded={showDropdown}
        aria-controls={showDropdown ? listboxId : undefined}
        aria-autocomplete="list"
        role="combobox"
      />

      {showDropdown &&
        dropdownAnchor &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            data-artist-search-dropdown
            className="art-premium-surface pointer-events-auto fixed z-[20050] overflow-hidden rounded-2xl shadow-[0_16px_40px_rgba(120,72,20,0.14)]"
            style={{
              left: dropdownAnchor.left,
              top: dropdownAnchor.top,
              width: dropdownAnchor.width,
            }}
            role="presentation"
          >
            {indexLoading ? (
              <p className="px-4 py-3 text-sm text-[#6B5948]">Loading artists…</p>
            ) : results.length === 0 ? (
              <p className="px-4 py-3 text-sm text-[#6B5948]">No artists found. Try another name.</p>
            ) : (
              <ul
                id={listboxId}
                role="listbox"
                className="max-h-[min(18rem,50vh)] overflow-y-auto py-1"
                aria-label="Artist search results"
              >
                {results.map((artist, i) => {
                  const isActive = i === activeIndex;
                  return (
                    <li key={artist.uid} role="option" aria-selected={isActive}>
                      <button
                        type="button"
                        onMouseEnter={() => setActiveIndex(i)}
                        onClick={() => goToArtist(artist)}
                        className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition ${
                          isActive ? 'bg-[#FFF9EF]' : 'hover:bg-[#FFF9EF]/90'
                        }`}
                      >
                        <span className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-[#F5D99B]/55 bg-[#f4efe6] ring-1 ring-white">
                          <ArtistSearchAvatar artist={artist} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-[#2F251B]">
                            {artist.displayName}
                          </span>
                          {artist.bio ? (
                            <span className="mt-0.5 block truncate text-xs text-[#6B5948]">
                              {artist.bio}
                            </span>
                          ) : artist.styles?.length ? (
                            <span className="mt-0.5 block truncate text-xs text-[#8A7357]">
                              {artist.styles.slice(0, 2).join(' · ')}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
